import React, { useMemo } from 'react'
import { Trans, Plural, useLingui } from '@lingui/react/macro'
import { graphql, useFragment } from 'react-relay'

import { XStack, YStack } from 'tamagui'

import { Paragraph, Strong } from '@newComponents/Text'
import { Stepper } from '@newComponents/Form/FormStepper'

import { ticketQuantityLimits } from './ticketQuantityLimits'

import { TierSelectionItem_tier$key } from './__generated__/TierSelectionItem_tier.graphql'

// Below this many tickets left, tell the buyer. Above it, a count reads as
// pressure rather than information.
const LOW_AVAILABILITY_THRESHOLD = 10

export const TierSelectionItem = ({
  tier,
  quantity,
  onQuantityChange,
}: {
  tier: TierSelectionItem_tier$key
  quantity: number
  onQuantityChange: (qty: number) => void
}) => {
  const { t } = useLingui()
  const data = useFragment(
    graphql`
      fragment TierSelectionItem_tier on EventTicketingTier {
        name
        description
        price
        currency
        includeTax
        taxName
        taxPercentage
        status
        availableFrom
        availableUntil
        ticketsRemaining
        minPerOrder
        maxPerOrder
      }
    `,
    tier
  )

  const {
    min: minQuantity,
    max: maxQuantity,
    available: isAvailable,
  } = ticketQuantityLimits(data)

  // null means the tier has no ticket_limit, so it never runs out. A tier with
  // fewer left than its own minimum per order cannot be bought either: the
  // smallest permitted order already exceeds what remains.
  const isSoldOut =
    data.ticketsRemaining != null && data.ticketsRemaining < minQuantity

  const unavailableReason = useMemo(() => {
    if (data.status !== 'active') return t`Sales paused`
    const now = new Date()
    if (data.availableFrom && new Date(data.availableFrom) > now)
      return t`Not yet available`
    if (data.availableUntil && new Date(data.availableUntil) < now)
      return t`Sales ended`
    if (isSoldOut) return t`Sold out`
    return undefined
  }, [data.status, data.availableFrom, data.availableUntil, isSoldOut, t])

  const formattedPrice = useMemo(() => {
    if (data.price === 0) return t`Free`

    const price = data.price / 100
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: data.currency,
        maximumFractionDigits: price % 1 === 0 ? 0 : undefined,
      }).format(price)
    } catch {
      return `${price} ${data.currency}`
    }
  }, [data.price, data.currency])

  const handleDecrement = () => {
    if (quantity <= minQuantity) {
      onQuantityChange(0)
    } else {
      onQuantityChange(quantity - 1)
    }
  }

  const handleIncrement = () => {
    if (quantity === 0) {
      onQuantityChange(minQuantity)
    } else {
      onQuantityChange(Math.min(maxQuantity, quantity + 1))
    }
  }

  return (
    <XStack gap='$4'>
      <YStack gap='$1' flex={1}>
        <Strong size='$5'>{data.name}</Strong>

        {data.description ? (
          <Paragraph size='$4'>{data.description}</Paragraph>
        ) : null}

        <XStack gap='$2' alignItems='center'>
          <Strong
            size='$6'
            fontWeight='$7'
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {formattedPrice}
          </Strong>

          {data.taxName && data.taxPercentage ? (
            <Paragraph size='$3' color='$colorSubtle'>
              {data.includeTax ? 'incl.' : 'excl.'} {data.taxName} @{' '}
              {(data.taxPercentage ?? 0) * 100}%
            </Paragraph>
          ) : null}
        </XStack>

        {minQuantity > 1 && (
          <Paragraph size='$3' color='$colorSubtle'>
            <Trans>Minimum {minQuantity} per order</Trans>
          </Paragraph>
        )}

        {isAvailable &&
          data.ticketsRemaining != null &&
          data.ticketsRemaining <= LOW_AVAILABILITY_THRESHOLD && (
            <Paragraph size='$4' color='$colorSubtle'>
              <Plural
                value={data.ticketsRemaining}
                one='Only # ticket left'
                other='Only # tickets left'
              />
            </Paragraph>
          )}
      </YStack>

      <YStack alignItems='center' justifyContent='center'>
        {isAvailable ? (
          <Stepper
            value={quantity}
            onChange={onQuantityChange}
            onDecrement={handleDecrement}
            onIncrement={handleIncrement}
            decrementDisabled={quantity <= 0}
            incrementDisabled={quantity >= maxQuantity}
          />
        ) : (
          <Paragraph size='$4' color='$colorMuted'>
            {unavailableReason}
          </Paragraph>
        )}
      </YStack>
    </XStack>
  )
}
